import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@platform/ui';
import { useLabel } from '@platform/hooks/useLabel';
import { routes } from '@platform/core/lib/routes';
import { CategoryRail } from '../CategoryRail';
import { ModuleSectionList } from '../ModuleSectionList';
import { moduleSections } from '../moduleSections';
import { usePins } from '../usePins';
import { appRoutes } from '../appRoutes';
import { useModuleDescriptions } from '../strings/useModuleDescriptions';
import { useShellNav } from '../useShellNav';
import type { IModuleNavigatorProps } from './IModuleNavigatorProps';
import { setNavigatorOpen } from './navigatorStore';
import { RecentModuleChips } from './RecentModuleChips';
import { useRecentModules } from './recentModules';
import { useNavigatorKeys } from './useNavigatorKeys';
import './navigator.css';

/**
 * Навігатор модулів (patterns/keyboard «Ctrl/⌘ K», layout/dialogs; прототип v4 G:L3674–3735): затемнення, діалог до 960px заввишки
 * min(680px, 86vh) з радіусом 14; угорі — пошук-пігулка 42px «N модулів» / «N з M» і кругла кнопка закриття; ліворуч — колонка
 * категорій 200px, праворуч — «Нещодавні» (без запиту й категорії), плитки груп і «Система»; унизу — підказка й «Керувати модулями →».
 * Закривається Esc, кліком по тлу, × і переходом на іншу сторінку; фокус повертається туди, звідки навігатор відкрили.
 */
export function ModuleNavigator({ ws }: IModuleNavigatorProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const { key: locationKey } = useLocation();
  const { me, catalog, parliament } = useShellNav(ws);
  const [pinned, togglePin] = usePins(ws);
  const recent = useRecentModules(me?.id, ws);
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const opened = useRef(locationKey);
  const close = () => setNavigatorOpen(false);
  useEffect(() => { if (locationKey !== opened.current) setNavigatorOpen(false); }, [locationKey]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    input.current?.focus();
    return () => previous?.focus?.();
  }, []);
  const describe = useModuleDescriptions(catalog, (catalog?.modules ?? []).filter((m) => m.installed).map((m) => m.key));
  const onSystem = (key: string) => { close(); navigate(key === 'administration' ? appRoutes.admin() : key === 'customization' ? appRoutes.customize() : routes.home()); };
  const base = { catalog, pinned, label, pinnedLabel: t('allModules.pinned'), systemLabel: t('allModules.system'), onSystem, describe, parliament };
  const sections = moduleSections({ ...base, q, group });
  const unique = (list: typeof sections) => new Set(list.flatMap((s) => s.cards.map((c) => c.key.split('/')[0]))).size;
  const total = unique(moduleSections({ ...base, q: '', group: '' }));
  const shown = unique(sections);
  const openFirst = () => {
    const first = sections[0]?.cards[0];
    if (first?.to) navigate(first.to); else first?.onOpen?.();
  };
  const onKeyDown = useNavigatorKeys(box, input, close, openFirst);
  return (
    <div className="nv-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div ref={box} className="nv-dialog" role="dialog" aria-modal="true" aria-label={t('navigator.title')} onKeyDown={onKeyDown}>
        <div className="nv-head">
          <label className="nv-search">
            <Icon name="search" size={16} strokeWidth={2} />
            <input ref={input} value={q} placeholder={t('navigator.placeholder')} aria-label={t('navigator.search')} onChange={(e) => setQ(e.target.value)} />
            <span className="nv-search__count">{shown >= total ? t('shell.modulesCount', { count: total }) : t('allModules.shown', { shown, count: total })}</span>
          </label>
          <button type="button" className="nv-close" aria-label={t('navigator.close')} title={t('navigator.closeTitle')} onClick={close}><Icon name="x" size={16} /></button>
        </div>
        <div className="nv-body">
          <div className="nv-rail"><CategoryRail ws={ws} q={q} group={group} onPick={setGroup} compact /></div>
          <div className="nv-pane">
            {!q.trim() && !group && <RecentModuleChips ws={ws} keys={recent} />}
            <ModuleSectionList sections={sections} onPin={togglePin} />
            {!sections.length && <div className="am-empty">{q ? t('allModules.empty', { q }) : t('allModules.emptyGroup')}</div>}
          </div>
        </div>
        <div className="nv-foot">
          <span>{t('navigator.hint')}</span>
          <Link className="nv-foot__link" to={routes.modules()}>{t('navigator.manage')}</Link>
        </div>
      </div>
    </div>
  );
}
